
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Filter } from "lucide-react";
import { type DeviceStatus } from "@/utils/deviceStatus";

export interface DeviceFilterOptions {
  status: DeviceStatus[];
  lastSyncRange: "all" | "1h" | "24h" | "7d" | "30d";
}

interface DeviceFiltersProps {
  currentFilters: DeviceFilterOptions;
  onFiltersChange: (filters: DeviceFilterOptions) => void;
}

export const DeviceFilters = ({ currentFilters, onFiltersChange }: DeviceFiltersProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const statusOptions: { value: DeviceStatus; label: string }[] = [
    { value: "active", label: "Active" },
    { value: "in_process", label: "In Process" },
    { value: "registered", label: "Registered" },
  ];

  const lastSyncOptions = [
    { value: "all" as const, label: "All Time" },
    { value: "1h" as const, label: "Last Hour" },
    { value: "24h" as const, label: "Last 24 Hours" },
    { value: "7d" as const, label: "Last 7 Days" },
    { value: "30d" as const, label: "Last 30 Days" },
  ];

  const handleStatusChange = (status: DeviceStatus, checked: boolean) => {
    const newStatus = checked
      ? [...currentFilters.status, status]
      : currentFilters.status.filter(s => s !== status);
    
    onFiltersChange({
      ...currentFilters,
      status: newStatus,
    });
  };

  const handleLastSyncChange = (range: DeviceFilterOptions["lastSyncRange"]) => {
    onFiltersChange({
      ...currentFilters,
      lastSyncRange: range,
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      status: [],
      lastSyncRange: "all",
    });
  };

  const hasActiveFilters = currentFilters.status.length > 0 || currentFilters.lastSyncRange !== "all";

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={hasActiveFilters ? "bg-blue-50 border-blue-200" : ""}>
          <Filter className="w-4 h-4 mr-2" />
          Filter
          {hasActiveFilters && (
            <span className="ml-1 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {currentFilters.status.length + (currentFilters.lastSyncRange !== "all" ? 1 : 0)}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Filters</h4>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear All
              </Button>
            )}
          </div>

          {/* Status Filter */}
          <div>
            <h5 className="text-sm font-medium mb-2">Status</h5>
            <div className="space-y-2">
              {statusOptions.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`status-${option.value}`}
                    checked={currentFilters.status.includes(option.value)}
                    onCheckedChange={(checked) => handleStatusChange(option.value, checked as boolean)}
                  />
                  <label
                    htmlFor={`status-${option.value}`}
                    className="text-sm font-normal"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Last Sync Filter */}
          <div>
            <h5 className="text-sm font-medium mb-2">Last Sync</h5>
            <div className="space-y-2">
              {lastSyncOptions.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`sync-${option.value}`}
                    checked={currentFilters.lastSyncRange === option.value}
                    onCheckedChange={() => handleLastSyncChange(option.value)}
                  />
                  <label
                    htmlFor={`sync-${option.value}`}
                    className="text-sm font-normal"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
